/**
 * Tactic Codes — alphanumeric serialisation of a `TacticsLayout` for
 * social sharing. EA FC 25 ships a similar mechanic ("Tactic Codes")
 * where coaches paste a short string in chat / Reddit and the
 * receiver loads the exact tactic into their game.
 *
 * Approach: JSON-stringify the layout, then base64url-encode the
 * resulting bytes (so the code is URL-safe and copyable from chat
 * without manual escaping). No compression in v1 — keep the codec
 * symmetric, sync, and dep-free. A future iteration could deflate
 * with `CompressionStream` to shrink long layouts (~50-70% smaller)
 * if codes start to feel unwieldy.
 *
 * Decoding is defensive: any parse failure, missing schema version,
 * or wrong-typed core field returns `null` rather than throwing — a
 * malformed paste shouldn't crash the board.
 *
 * The codec is intentionally tied to the public `TacticsLayout`
 * shape, so a code minted from one app version stays loadable across
 * minor versions as long as `v` round-trips and missing optional
 * fields keep defaulting on the consumer side (which is already how
 * `tactics3dStore` loads layouts).
 */

import { TACTICS_SCHEMA_VERSION, type TacticsLayout } from './types';

/** Lookup table for base64-url (RFC 4648 §5) — same as standard
 *  base64 but `-` / `_` instead of `+` / `/`, no padding. */
const ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Encode a UTF-8 string to base64url (no padding, no newlines). */
function utf8ToBase64Url(input: string): string {
  // TextEncoder is available in all modern browsers + Node 11+.
  const bytes =
    typeof TextEncoder !== 'undefined'
      ? new TextEncoder().encode(input)
      : new Uint8Array(Buffer.from(input, 'utf-8'));
  let out = '';
  let i = 0;
  while (i + 2 < bytes.length) {
    const a = bytes[i++]!;
    const b = bytes[i++]!;
    const c = bytes[i++]!;
    out += ALPHABET[a >> 2]!;
    out += ALPHABET[((a & 3) << 4) | (b >> 4)]!;
    out += ALPHABET[((b & 15) << 2) | (c >> 6)]!;
    out += ALPHABET[c & 63]!;
  }
  if (i < bytes.length) {
    const a = bytes[i++]!;
    out += ALPHABET[a >> 2]!;
    if (i < bytes.length) {
      const b = bytes[i++]!;
      out += ALPHABET[((a & 3) << 4) | (b >> 4)]!;
      out += ALPHABET[(b & 15) << 2]!;
    } else {
      out += ALPHABET[(a & 3) << 4]!;
    }
  }
  return out;
}

/** Decode base64url back to a UTF-8 string. Returns null on invalid
 *  input (unknown character, dangling 1-character tail). */
function base64UrlToUtf8(input: string): string | null {
  // Build the reverse lookup lazily but cache via module scope.
  // Index 64 sentinel = invalid.
  let bytes: Uint8Array | null = null;
  try {
    const len = input.length;
    // Output byte length: full quartets give 3 bytes; remainder bits
    // give 1 or 2 more.
    const fullQuartets = Math.floor(len / 4);
    const rem = len - fullQuartets * 4;
    let outLen = fullQuartets * 3;
    if (rem === 2) outLen += 1;
    else if (rem === 3) outLen += 2;
    else if (rem === 1) return null;
    bytes = new Uint8Array(outLen);
    let bi = 0;
    let i = 0;
    while (i + 3 < len) {
      const a = ALPHABET.indexOf(input[i++]!);
      const b = ALPHABET.indexOf(input[i++]!);
      const c = ALPHABET.indexOf(input[i++]!);
      const d = ALPHABET.indexOf(input[i++]!);
      if (a < 0 || b < 0 || c < 0 || d < 0) return null;
      bytes[bi++] = (a << 2) | (b >> 4);
      bytes[bi++] = ((b & 15) << 4) | (c >> 2);
      bytes[bi++] = ((c & 3) << 6) | d;
    }
    if (rem === 2) {
      const a = ALPHABET.indexOf(input[i++]!);
      const b = ALPHABET.indexOf(input[i++]!);
      if (a < 0 || b < 0) return null;
      bytes[bi++] = (a << 2) | (b >> 4);
    } else if (rem === 3) {
      const a = ALPHABET.indexOf(input[i++]!);
      const b = ALPHABET.indexOf(input[i++]!);
      const c = ALPHABET.indexOf(input[i++]!);
      if (a < 0 || b < 0 || c < 0) return null;
      bytes[bi++] = (a << 2) | (b >> 4);
      bytes[bi++] = ((b & 15) << 4) | (c >> 2);
    }
  } catch {
    return null;
  }
  if (!bytes) return null;
  try {
    return typeof TextDecoder !== 'undefined'
      ? new TextDecoder('utf-8', { fatal: true }).decode(bytes)
      : Buffer.from(bytes).toString('utf-8');
  } catch {
    return null;
  }
}

/** Serialise a layout into a URL-safe alphanumeric code. */
export function encodeTacticCode(layout: TacticsLayout): string {
  // JSON.stringify drops `undefined` fields and uses no whitespace.
  return utf8ToBase64Url(JSON.stringify(layout));
}

/** Parse a code back into a `TacticsLayout`. Returns `null` on any
 *  invalid input — caller decides whether to show an error toast or
 *  silently ignore. */
export function decodeTacticCode(code: string): TacticsLayout | null {
  if (typeof code !== 'string' || code.length === 0) return null;
  // Strip whitespace the user might have introduced when copy-pasting
  // across chat clients (some wrap long strings).
  const cleaned = code.replace(/\s+/g, '');
  const json = base64UrlToUtf8(cleaned);
  if (json === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  // Minimal structural validation — anything beyond this falls
  // through to the consumer's own type guards.
  if (typeof obj.v !== 'number' || obj.v > TACTICS_SCHEMA_VERSION) return null;
  if (typeof obj.homeFormation !== 'string') return null;
  if (typeof obj.awayFormation !== 'string') return null;
  if (!Array.isArray(obj.homeSpots)) return null;
  if (!Array.isArray(obj.awaySpots)) return null;
  if (!Array.isArray(obj.arrows)) return null;
  if (typeof obj.saved_at !== 'string') return null;
  return obj as unknown as TacticsLayout;
}
